import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import { useAppDispatch } from '../../../hooks/redux';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';
import Modal from '../../common/modal/modal';
import { IJournalResult } from '../../../store/types';

import { availableFigures, Figure, PlacedFigure } from './types';
import { FigureRenderer } from './FigureRenderer';
import { SandTherapyBoard } from './SandTherapyBoard';

interface SandTherapyPsyProps {
  roomName?: string;
  sendSandTherapyOpen?: () => void;
  sendSandTherapyClose?: () => void;
  placedFigures: PlacedFigure[];
  sandColor: string;
  clientReady: boolean;
  onCloseTool?: () => void;
  result?: IJournalResult;
  setResult?: (value: IJournalResult) => void;
}

const FIGURE_CATEGORIES = [
  { label: 'Семья — Пожилые', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-elder' },
  { label: 'Семья — Взрослые', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-adult' },
  { label: 'Семья — Подростки', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-teen' },
  { label: 'Семья — Дети', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-child' },
  { label: 'Семья — Младенцы', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-baby' },
  { label: 'Эмоции — Спокойные', filter: (f: Omit<Figure, 'id'>) => f.type === 'emotional-calm' },
  { label: 'Эмоции — Расстроенные', filter: (f: Omit<Figure, 'id'>) => f.type === 'emotional-upset' },
  { label: 'Эмоции — Радостные', filter: (f: Omit<Figure, 'id'>) => f.type === 'emotional-happy' },
  { label: 'Дома', filter: (f: Omit<Figure, 'id'>) => f.type === 'house' },
  { label: 'Деревья', filter: (f: Omit<Figure, 'id'>) => f.type === 'tree' },
  {
    label: 'Животные',
    filter: (f: Omit<Figure, 'id'>) => ['animal-dog', 'animal-cat', 'animal-bird'].includes(f.type),
  },
  { label: 'Специальные', filter: (f: Omit<Figure, 'id'>) => ['cocoon', 'striped'].includes(f.type) },
  { label: 'Люди', filter: (f: Omit<Figure, 'id'>) => f.type === 'person' || f.type === 'tall' },
  { label: 'Шары', filter: (f: Omit<Figure, 'id'>) => f.type === 'ball' },
];

function getFigureHeightClass(size: Figure['size']) {
  switch (size) {
    case 'small':
      return 'h-10';
    case 'medium':
      return 'h-14';
    case 'large':
      return 'h-16';
  }
}

export const SandTherapyPsy: FC<SandTherapyPsyProps> = ({
  sendSandTherapyOpen,
  sendSandTherapyClose,
  placedFigures,
  sandColor,
  clientReady,
  onCloseTool,
  result,
  setResult,
}) => {
  const dispatch = useAppDispatch();
  const [active, setActive] = useState(false);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  const handleStartSession = useCallback(() => {
    sendSandTherapyOpen?.();
    dispatch(setActiveTool(PsyToolsId.SAND_THERAPY));
    setActive(true);
  }, [sendSandTherapyOpen, dispatch]);

  const handleRequestClose = useCallback(() => {
    setIsConfirmCloseOpen(true);
  }, []);

  const handleConfirmClose = useCallback(() => {
    if (result && setResult && placedFigures.length > 0) {
      setResult({
        ...result,
        sand_therapy: [
          ...(result.sand_therapy ?? []),
          {
            date_time: new Date().toISOString(),
            placed_figures: placedFigures,
            sand_color: sandColor,
            comment: '',
          },
        ],
      });
    }
    setIsConfirmCloseOpen(false);
    sendSandTherapyClose?.();
    dispatch(setActiveTool(0));
    setActive(false);
    onCloseTool?.();
  }, [result, setResult, placedFigures, sandColor, sendSandTherapyClose, dispatch, onCloseTool]);

  const handleCancelClose = useCallback(() => {
    setIsConfirmCloseOpen(false);
  }, []);

  const previewFigures = useMemo(() => availableFigures.slice(0, 12), []);

  if (active) {
    return (
      <div className="w-[1200px] min-w-[1200px] h-full flex flex-col text-white font-comfortaa p-6 relative">
        <h2 className="text-2xl font-bold mb-4">Песочная терапия</h2>

        <div className="flex-1 min-h-0 rounded-lg overflow-hidden">
          <SandTherapyBoard
            selectedFigures={[]}
            placedFigures={placedFigures}
            onPlaceFigure={() => {}}
            onMoveFigure={() => {}}
            onRotateFigure={() => {}}
            onResizeFigure={() => {}}
            onRemoveFigure={() => {}}
            sandColor={sandColor}
            readonly
          />
        </div>

        <div className="absolute bottom-6 left-6 right-6 flex gap-3">
          {clientReady ? (
            <div className="flex-1 flex-col items-center justify-center px-2 py-2">
              <div className="w-full bg-green-100 border border-green-400 text-green-700 px-3 py-2 rounded-[10px] text-base text-center font-bold mb-1.5">
                Клиент завершил работу
              </div>
              <button
                onClick={handleRequestClose}
                className="w-full py-3 rounded-[10px] bg-[#C00000] text-white font-bold text-base cursor-pointer hover:bg-[#a00000] transition-colors"
              >
                Завершить работу с инструментом
              </button>
            </div>
          ) : (
            <div className="flex-1 flex-col items-center justify-center px-2 py-2">
              <button
                disabled
                className="w-full py-3 rounded-[10px] bg-[#C00000]/50 text-white font-bold text-base cursor-not-allowed"
              >
                <span className="animate-pulse">Клиент работает…</span>
              </button>
            </div>
          )}
        </div>

        <Modal
          isConfirmCloseOpen={isConfirmCloseOpen}
          cancelCloseTool={handleConfirmClose}
          confirmCloseTool={handleCancelClose}
          confirmMessage="Вы действительно хотите закрыть инструмент «Песочная терапия»? Клиент перестанет видеть инструмент."
          title="Закрытие инструмента"
          cancelText="Оставить открытым"
          nonCancelText="Закрыть инструмент"
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col text-white font-comfortaa pt-6 px-6 relative">
      <p className="text-2xl font-bold mb-4">Песочная терапия</p>
      <div className="text-base font-manrope leading-relaxed mb-6">
        Предложите клиенту выбрать фигуры из набора и расставить их на виртуальном песочном поле.
        <br />
        <br />
        Полученная расстановка служит проекцией для дальнейшей терапевтической беседы.
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        <div className="grid grid-cols-4 gap-2 pb-[100px]">
          {previewFigures.map((fig, idx) => (
            <div key={idx} className="flex items-center justify-center bg-white/10 rounded-lg p-2 h-16">
              <div className={classNames('w-8', getFigureHeightClass(fig.size))}>
                <FigureRenderer figure={fig} className="w-full h-full" />
              </div>
            </div>
          ))}
          <div className="flex items-center justify-center bg-white/10 rounded-lg p-2 h-16 text-white/50 text-xs text-center">
            +{availableFigures.length - previewFigures.length} фигур
          </div>
        </div>
      </div>

      <div className="absolute bottom-[24px] left-6 right-8">
        <button
          onClick={handleStartSession}
          className="w-full h-[57px] py-4 rounded-[16px] bg-[#C00000] text-white font-comfortaa font-bold text-[20px] hover:bg-[#a00000] transition-colors cursor-pointer"
        >
          Начать сессию
        </button>
      </div>
    </div>
  );
};
